import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Crown, Lock, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, Reveal } from "@/components/shared";

const fmt = (n) => Number(n).toFixed(2).replace(".", ",") + " €";

export default function SoireeVip() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Réservez ma table VIP — Inauguration YouAussi & YouAussi Media | Palais Maasaï";
    return () => {
      document.title = "Palais Maasaï";
    };
  }, []);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [sending, setSending] = useState(false);
  const [payment, setPayment] = useState("idle"); // idle | checking | paid | pending | error
  const [paidResa, setPaidResa] = useState(null);

  const load = () => api.get("/soiree-vip").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setPayment("checking");
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data: ps } = await api.get(`/soiree-vip/payment-status/${sessionId}`);
        if (ps.payment_status === "paid") {
          setPaidResa(ps.reservation);
          setPayment("paid");
          load();
          return;
        }
      } catch {
        setPayment("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) timer = setTimeout(poll, 2000);
      else setPayment("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const table = data?.tables?.find((t) => t.id === selected);

  const pay = async () => {
    if (!table) return;
    if (!form.name || !form.email || !form.phone) {
      toast.error("Merci d'indiquer votre nom, votre email et votre téléphone.");
      return;
    }
    if (!acceptTerms) {
      toast.error("Vous devez accepter la condition de non-remboursement de l'acompte.");
      return;
    }
    setSending(true);
    try {
      const { data: res } = await api.post("/soiree-vip/reservations", {
        table_id: table.id,
        ...form,
        accept_terms: acceptTerms,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
      load();
    }
  };

  return (
    <div data-testid="soiree-vip-page">
      <PageHero overline="À la une" title="Réservez ma table VIP" testId="vip-hero">
        <p>Inauguration YouAussi &amp; YouAussi Media — DJ Samir · Afrobeat vs Années 80-2000 · Samedi 10 octobre 2026, jusqu'à 5 h du matin · Palais Maasaï, Dijon.</p>
      </PageHero>

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-3xl">
          {payment === "paid" && paidResa && (
            <Reveal>
              <div className="rounded-lg border border-gold/40 bg-coal/60 p-10 text-center" data-testid="vip-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                  <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                </span>
                <h2 className="mt-6 text-2xl font-semibold text-ivory">Votre table VIP est confirmée</h2>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/60">
                  {paidResa.table_label} — {paidResa.capacity} personnes · Total {fmt(paidResa.price)} ·
                  Acompte réglé {fmt(paidResa.deposit)} · Solde sur place {fmt(paidResa.balance_due)}.
                  Un e-mail de confirmation vient de vous être envoyé.
                </p>
                <Link to="/" className="btn-outline mt-8" data-testid="vip-success-home">Retour à l'accueil</Link>
              </div>
            </Reveal>
          )}

          {payment === "checking" && (
            <p className="text-center text-sm text-ivory/60" data-testid="vip-checking">Vérification du paiement…</p>
          )}
          {payment === "pending" && (
            <p className="text-center text-sm text-ivory/60" data-testid="vip-pending">Paiement en cours de traitement — vous recevrez un e-mail de confirmation.</p>
          )}
          {payment === "error" && (
            <p className="text-center text-sm text-red-400" data-testid="vip-error">Paiement non confirmé — la table a été libérée. Vous pouvez réessayer ci-dessous.</p>
          )}

          {data && payment !== "paid" && (
            <>
              {data.closed ? (
                <Reveal>
                  <div className="rounded-lg border border-white/10 bg-coal/60 p-10 text-center" data-testid="vip-closed">
                    <Lock className="mx-auto h-8 w-8 text-ivory/40" strokeWidth={1.5} />
                    <h2 className="mt-4 text-xl font-semibold text-ivory">Réservations clôturées</h2>
                    <p className="mt-3 text-sm text-ivory/50">
                      Les réservations pour la soirée du 10 octobre sont closes depuis le samedi 10 octobre 2026 à 15 h.
                    </p>
                  </div>
                </Reveal>
              ) : data.sold_out ? (
                <Reveal>
                  <div className="rounded-lg border border-gold/30 bg-coal/60 p-10 text-center" data-testid="vip-soldout">
                    <Crown className="mx-auto h-8 w-8 text-gold" strokeWidth={1.5} />
                    <h2 className="mt-4 text-xl font-semibold text-ivory">Les deux tables VIP sont réservées</h2>
                    <p className="mt-3 text-sm text-ivory/50">Merci pour votre enthousiasme — contactez-nous pour les prochaines soirées.</p>
                  </div>
                </Reveal>
              ) : (
                <>
                  <Reveal>
                    <p className="mb-6 text-center text-sm text-ivory/60" data-testid="vip-remaining">
                      <strong className="text-gold">{data.remaining} table{data.remaining > 1 ? "s" : ""} VIP</strong> encore disponible{data.remaining > 1 ? "s" : ""} — deux tables VIP au total, pour 6 personnes chacune.
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2" data-testid="vip-tables">
                      {data.tables.map((t) => (
                        <button
                          key={t.id}
                          disabled={!t.available}
                          onClick={() => setSelected(t.id)}
                          className={`rounded-lg border p-6 text-left transition-all ${!t.available ? "cursor-not-allowed border-white/5 opacity-45" : selected === t.id ? "border-copper bg-copper/10" : "border-white/10 bg-coal/60 hover:border-gold/40"}`}
                          data-testid={`vip-table-${t.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="flex items-center gap-2 text-lg font-semibold text-ivory">
                              <Crown className="h-5 w-5 text-gold" strokeWidth={1.5} />
                              {t.label}
                            </h3>
                            {!t.available && <span className="text-[11px] font-bold uppercase tracking-wider text-red-400">Indisponible</span>}
                          </div>
                          <p className="mt-2 flex items-center gap-1.5 text-xs text-ivory/50">
                            <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                            {t.capacity} personnes
                          </p>
                          <div className="mt-4 space-y-1 text-sm">
                            <p className="flex justify-between text-ivory/70"><span>Prix total</span><strong className="text-ivory">{fmt(t.price)}</strong></p>
                            <p className="flex justify-between text-ivory/70"><span>Acompte en ligne</span><strong className="text-gold">{fmt(t.deposit)}</strong></p>
                            <p className="flex justify-between text-ivory/70"><span>Solde sur place</span><strong className="text-ivory">{fmt(t.balance)}</strong></p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </Reveal>

                  {table && (
                    <Reveal>
                      <div className="mt-8 space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="vip-form">
                        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-ivory">Vos coordonnées</h3>
                        <div className="rounded-md border border-gold/25 bg-gold/5 p-4 text-xs leading-relaxed text-ivory/70" data-testid="vip-recap">
                          <strong className="text-ivory">{table.label}</strong> — {table.capacity} personnes ·
                          Prix total <strong className="text-ivory">{fmt(table.price)}</strong> ·
                          Acompte à régler maintenant <strong className="text-gold">{fmt(table.deposit)}</strong> ·
                          Solde sur place <strong className="text-ivory">{fmt(table.balance)}</strong>.
                        </div>
                        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nom et prénom" className="input-dark" data-testid="vip-name" />
                        <div className="grid gap-4 sm:grid-cols-2">
                          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Adresse e-mail" className="input-dark !h-12" data-testid="vip-email" />
                          <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Téléphone" className="input-dark !h-12" data-testid="vip-phone" />
                        </div>
                        <label className="flex cursor-pointer items-start gap-3 rounded-md border border-white/10 p-4" data-testid="vip-terms-label">
                          <input
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            className="mt-0.5 accent-[#C4704F]"
                            data-testid="vip-terms-checkbox"
                          />
                          <span className="text-xs leading-relaxed text-ivory/70">
                            J'accepte que l'acompte de {fmt(table.deposit)} soit <strong className="text-ivory">non remboursable</strong> en cas d'annulation de ma part. Le solde de {fmt(table.balance)} sera réglé sur place le soir de l'événement.
                          </span>
                        </label>
                        <button onClick={pay} disabled={sending} className="btn-primary w-full" data-testid="vip-pay-button">
                          <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                          {sending ? "Redirection…" : `Payer l'acompte de ${fmt(table.deposit)} — Réservez ma table VIP`}
                        </button>
                        <p className="text-center text-[11px] text-ivory/35">
                          Paiement sécurisé par Stripe. La table n'est confirmée qu'après validation du paiement. Réservations closes le samedi 10 octobre 2026 à 15 h.
                        </p>
                      </div>
                    </Reveal>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
